import type { Order, OrderStatus } from '../../../graphql/types';
import { FlattenedOrderStatuses } from '../../../graphql/types';

export const getDerivedOrderStatuses = ({
  order,
  wantedTime,
  flattenedOrderStatus = FlattenedOrderStatuses.Failed,
}: GetDerivedOrderStatusesProps) => {
  const elapsed = Date.now() - new Date(wantedTime ?? '').getTime();
  const isCompleted = flattenedOrderStatus === 'completed';
  const canBeLate = !['completed', 'failed'].includes(flattenedOrderStatus);
  const isLate = elapsed > LATE_ORDER_THRESHOLD && canBeLate;
  const isPast = elapsed > PAST_ORDER_THRESHOLD && isCompleted;
  const isPendingFeedback = Boolean(order?.isPendingFeedback);
  const isTrackable = Boolean(order?.canTrackOrderStatus);

  return { isLate, isPast, isPendingFeedback, isTrackable };
};

/**
 * It is possible for an order to be stuck in the received state
 * way passed the time it is supposed to have been completed.
 *
 * In this scenario we choose to show the order as completed
 * so as to cause the least amount of friction for the customer.
 *
 * This helper will overwrite non-failed order statuses to completed
 * if the order wanted time is over three hours past.
 */
export const overwriteOrderStatus = (orderStatus: OrderStatus) => {
  // getting wanted time from order status > order > wanted time.
  const order = orderStatus?.order ?? {};
  const status = orderStatus?.flattenedOrderStatus;
  const wantedTime = order?.wantedTime ?? '';

  // Don't overwrite order status for invalid wanted times.
  if (!wantedTime) return orderStatus.flattenedOrderStatus;

  // checking if the order is overdue (not in a failed state and 3hrs over time)
  const canBeOverdue = status !== FlattenedOrderStatuses.Failed;
  const elapsed = Date.now() - new Date(wantedTime).getTime();
  const isOverdue = elapsed > OVERDUE_ORDER_THRESHOLD && canBeOverdue;

  // overwrite the status to completed when the order is overdue.
  if (isOverdue) return FlattenedOrderStatuses.Completed;

  // no changes needed to the remote status.
  return orderStatus.flattenedOrderStatus;
};

const LATE_ORDER_THRESHOLD = 1000 * 60 * 10; // 10 mins.
const PAST_ORDER_THRESHOLD = 1000 * 60 * 60 * 3; // 3 hours.
const OVERDUE_ORDER_THRESHOLD = 1000 * 60 * 60 * 3; // 3 hours.

type GetDerivedOrderStatusesProps = {
  readonly order?: Partial<Order>;
  readonly wantedTime?: string;
  readonly flattenedOrderStatus?: FlattenedOrderStatuses;
};
